import React, { useMemo, useState } from 'react';
import { Building, TrendingUp, Calculator as CalculatorIcon, DollarSign } from 'lucide-react';
import Card from '../../components/UI/Card';
import CalculatorInput from '../../components/Tools/CalculatorInput';
import useTranslation from '../../hooks/useTranslation';

const initialInputs = {
  purchase: '200000',
  rehab: '30000',
  rent: '2800',
  vacancy: '5',
  expenses: '6800',
  capRate: '6',
  ltv: '75',
  payoff: '230000',
  rate: '7.25',
  term: '30',
};

const parseNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value, decimals = 0) => {
  const safeValue = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(safeValue);
};

const calculateBRRR = (inputs) => {
  const purchase = parseNumber(inputs.purchase);
  const rehab = parseNumber(inputs.rehab);
  const rent = parseNumber(inputs.rent);
  const vacancyRate = parseNumber(inputs.vacancy) / 100;
  const expenses = parseNumber(inputs.expenses);
  const capRate = parseNumber(inputs.capRate) / 100;

  const totalCost = purchase + rehab;
  const grossAnnualRent = rent * 12;
  const vacancyAmount = grossAnnualRent * vacancyRate;
  const effectiveIncome = grossAnnualRent - vacancyAmount;
  const noi = effectiveIncome - expenses;
  const value = capRate > 0 ? noi / capRate : 0;

  const ltv = parseNumber(inputs.ltv) / 100;
  const payoff = parseNumber(inputs.payoff);
  const maxLoan = value * ltv;
  const cashout = maxLoan - payoff;

  const annualRate = parseNumber(inputs.rate) / 100;
  const termYears = parseNumber(inputs.term);
  const monthlyRate = annualRate / 12;
  const totalPayments = termYears * 12;
  const loanAmount = Math.max(0, maxLoan);
  const payment = monthlyRate > 0
    ? loanAmount * ((monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / (Math.pow(1 + monthlyRate, totalPayments) - 1))
    : totalPayments > 0
      ? loanAmount / totalPayments
      : 0;

  const monthlyOpex = expenses / 12;
  const cashflow = rent - monthlyOpex - payment;
  const annualDebtService = payment * 12;
  const dscr = annualDebtService > 0 ? noi / annualDebtService : 0;

  let dscrStatus = 'risky';
  if (dscr >= 1.25) {
    dscrStatus = 'solid';
  } else if (dscr >= 1.1) {
    dscrStatus = 'acceptable';
  }

  return {
    totalCost,
    grossAnnualRent,
    vacancyAmount,
    effectiveIncome,
    noi,
    value,
    maxLoan,
    cashout,
    payment,
    cashflow,
    dscr,
    dscrStatus,
  };
};

const dscrStyles = {
  solid: {
    value: 'text-green-600 dark:text-green-400',
    badge: 'border-green-200 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/10',
  },
  acceptable: {
    value: 'text-amber-600 dark:text-amber-300',
    badge: 'border-amber-200 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10',
  },
  risky: {
    value: 'text-red-600 dark:text-red-400',
    badge: 'border-red-200 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10',
  },
};

const BRRRCalculator = () => {
  const [inputs, setInputs] = useState(initialInputs);
  const { t } = useTranslation();

  const results = useMemo(() => calculateBRRR(inputs), [inputs]);

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const dscrStyle = dscrStyles[results.dscrStatus] ?? dscrStyles.risky;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Building className="h-5 w-5 text-primary-600" />
          {t('brrr.title', 'BRRR Calculator (Buy → Rehab → Rent → Refinance → Repeat)')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('brrr.description', 'Analyze a BRRR project, estimate NOI, market value, loan proceeds, and DSCR before refinancing.')}
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 space-y-6">
          <div className="flex items-center gap-2">
            <CalculatorIcon className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('brrr.section.project')}</h3>
          </div>
          <div className="space-y-4">
            <CalculatorInput
              label={t('brrr.input.purchase')}
              value={inputs.purchase}
              onChange={(value) => handleInputChange('purchase', value)}
              prefix="$"
            />
            <CalculatorInput
              label={t('brrr.input.rehab')}
              value={inputs.rehab}
              onChange={(value) => handleInputChange('rehab', value)}
              prefix="$"
            />
            <CalculatorInput
              label={t('brrr.input.rent')}
              value={inputs.rent}
              onChange={(value) => handleInputChange('rent', value)}
              prefix="$"
            />
            <CalculatorInput
              label={t('brrr.input.vacancy')}
              value={inputs.vacancy}
              onChange={(value) => handleInputChange('vacancy', value)}
              suffix="%"
            />
            <CalculatorInput
              label={t('brrr.input.expenses')}
              value={inputs.expenses}
              onChange={(value) => handleInputChange('expenses', value)}
              prefix="$"
            />
            <CalculatorInput
              label={t('brrr.input.capRate')}
              value={inputs.capRate}
              onChange={(value) => handleInputChange('capRate', value)}
              suffix="%"
            />
          </div>
          <div className="bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.totalCost')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.totalCost)}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 space-y-6">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('brrr.section.loan')}</h3>
          </div>
          <div className="space-y-4">
            <CalculatorInput
              label={t('brrr.input.ltv')}
              value={inputs.ltv}
              onChange={(value) => handleInputChange('ltv', value)}
              suffix="%"
            />
            <CalculatorInput
              label={t('brrr.input.payoff')}
              value={inputs.payoff}
              onChange={(value) => handleInputChange('payoff', value)}
              prefix="$"
            />
            <CalculatorInput
              label={t('brrr.input.rate')}
              value={inputs.rate}
              onChange={(value) => handleInputChange('rate', value)}
              suffix="%"
            />
            <CalculatorInput
              label={t('brrr.input.term')}
              value={inputs.term}
              onChange={(value) => handleInputChange('term', value)}
            />
          </div>
          <div className="space-y-3 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.value')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.value)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.maxLoan')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.maxLoan)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.cashout')}</span>
              <span className={`font-semibold ${results.cashout >= 0 ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300'}`}>
                {formatCurrency(results.cashout)}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-6 space-y-6">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('brrr.section.metrics')}</h3>
          </div>
          <div className="space-y-3 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.grossIncome')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.grossAnnualRent)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.vacancy')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.vacancyAmount)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.effectiveIncome')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.effectiveIncome)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.noi')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.noi)}</span>
            </div>
          </div>
          <div className="space-y-3 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.payment')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.payment, 2)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.cashflow')}</span>
              <span className={`font-semibold ${results.cashflow >= 0 ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300'}`}>
                {formatCurrency(results.cashflow, 2)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('brrr.output.dscr')}</span>
              <span className="flex items-center gap-2">
                <span className={`font-semibold ${dscrStyle.value}`}>{results.dscr.toFixed(2)}</span>
                <span className={`text-xs font-medium uppercase tracking-wide border px-2 py-0.5 rounded-full ${dscrStyle.badge}`}>
                  {t(`brrr.dscr.${results.dscrStatus}`)}
                </span>
              </span>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CalculatorIcon className="w-5 h-5 text-primary-600" />
          {t('brrr.section.formulas')}
        </h3>
        <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-300 space-y-1">
          <li>{t('brrr.formula.noi')}</li>
          <li>{t('brrr.formula.value')}</li>
          <li>{t('brrr.formula.maxLoan')}</li>
          <li>{t('brrr.formula.dscr')}</li>
        </ul>
      </Card>
    </div>
  );
};

export default BRRRCalculator;
